import { useEffect, useReducer, useRef, type ReactNode } from 'react';
import { track } from '@citibox-panel/analytics';
import {
  ApiError,
  type DeliveryDetail,
  type LocationMatch,
  type UserSearchHit,
} from '@citibox-panel/api-client';
import { AddressMismatchAlert } from '../components/AddressMismatchAlert';
import { isAddressMismatch } from '../lib/isAddressMismatch';
import { addressMismatchRow, type AddressMismatchReport } from '../lib/addressMismatchRow';
import {
  addressMismatchFlow,
  initialAddressMismatchState,
  type AddressMismatchCheck,
  type AddressMismatchOutcome,
} from '../model/addressMismatchFlow';
import { useAddressMismatchCheck } from './useAddressMismatchCheck';
import { useReportAddressMismatch } from './useReportAddressMismatch';
import { useTitularAddress } from './useTitularAddress';
import { useTitularLocation } from './useTitularLocation';

export type ReportAddressMismatch = (
  report: AddressMismatchReport,
) => Promise<AddressMismatchOutcome>;

export type CheckAddressMismatchReport = (tracking: string) => Promise<AddressMismatchCheck>;

// What the mutation hook is handed when the host offers no send. Never called — without the send
// the warning renders no button — but a hook cannot be skipped, so it needs a function to hold.
// (The panel's own host always passes the send; an unconfigured environment answers it with the
// «sheet not found» notice instead of hiding the button.)
const reportNowhere: ReportAddressMismatch = () =>
  Promise.reject(new ApiError(0, 'SHEETS_NOT_FOUND', null, 'No mismatch sheet is configured'));

// «Notificar mismatch», whole: the titular's read, the comparison, the flow, the send, both
// analytics events, and the warning node the card places under the «buzón». Undefined while there is
// nothing to warn about — no detail yet, the titular's read in flight, failed or empty, or the
// titular belonging to the delivery's own ubicación. The button is offered only while the tracking
// is NOT in the sheet yet (useAddressMismatchCheck); nothing ever sends a tracking that is already
// there.
//
// The comparison is by UBICACIÓN whenever it can be: the titular's address is placed with the
// address search (useTitularLocation) and its id set against the delivery's. That is what catches
// two portals of one street number that are different Citiboxes, and what keeps quiet for a titular
// in another portal of the delivery's own building — the delivery's address always names the main
// portal, so the text alone cannot tell those two apart. Street and number (isAddressMismatch)
// decide only when there is no identity to compare: no delivery id, or a titular address the search
// cannot place. While the search is in flight the warning waits, so it never shows and then leaves.
//
// Called by DeliveryLayout, ABOVE the skeleton a refetch raises: the reducer and the detection
// dedupe live in this hook's slot of the layout, so the success line (and its row number) survives
// the remount and a refetch never counts the same detection twice (ADR 0013). A hook inside the
// warning itself would unmount with the skeleton and lose both.
export function useAddressMismatch(
  deliveryId: string,
  detail: DeliveryDetail | undefined,
  searchUsersByPhone: (phone: string) => Promise<UserSearchHit[]>,
  searchLocations: (q: string) => Promise<LocationMatch[]>,
  reportAddressMismatch: ReportAddressMismatch | undefined,
  checkAddressMismatchReport: CheckAddressMismatchReport | undefined,
): ReactNode | undefined {
  // Read without blocking and without throwing: the detail never waits on it, never falls with it.
  const titularAddress = useTitularAddress(detail?.recipient ?? null, searchUsersByPhone);
  // No delivered address, no warning — even when the ids alone could tell the titular is elsewhere:
  // it would read «Entregada en:» with nothing after it, and send Amazon a row with that cell empty.
  // So the titular is not placed either; that read would decide nothing.
  const hasDeliveredAddress = (detail?.location.address ?? '').trim() !== '';
  const deliveredLocationId = detail?.location.id ?? null;
  const titularLocation = useTitularLocation(
    titularAddress,
    searchLocations,
    hasDeliveredAddress && deliveredLocationId !== null,
  );
  const [state, dispatch] = useReducer(addressMismatchFlow, initialAddressMismatchState);
  const mutation = useReportAddressMismatch(reportAddressMismatch ?? reportNowhere);
  const detected =
    detail !== undefined &&
    titularAddress !== null &&
    hasDeliveredAddress &&
    titularLocation.status !== 'pending' &&
    (titularLocation.status === 'resolved'
      ? titularLocation.id !== deliveredLocationId
      : isAddressMismatch(detail.location.address, titularAddress));
  const report = detected ? addressMismatchRow(detail, titularAddress) : null;
  const check = useAddressMismatchCheck(
    report?.tracking ?? '',
    checkAddressMismatchReport,
    report !== null && reportAddressMismatch !== undefined,
  );
  // `carrier` is nullable on the wire, so it travels as an explicit token, never `String(undefined)`.
  const carrierToken = detail?.carrier?.name ?? 'unknown';

  // Once per «Entrega». Dimensions ABOUT the detection, never its subjects: no address, phone,
  // name or id.
  const detectionTracked = useRef<string | null>(null);
  useEffect(() => {
    if (!detected || detectionTracked.current === deliveryId) return;
    detectionTracked.current = deliveryId;
    track('address_mismatch_detected', { carrier: carrierToken });
  }, [detected, deliveryId, carrierToken]);

  if (report === null) return undefined;

  // ONE report, ONE event: the ratio reported ÷ detected must not count one report twice.
  // `duplicate_warned` is the terminal outcome of a send that found the tracking already in the
  // sheet and wrote nothing — nothing offers to force it. A tracking the check found before any
  // click records nothing: no report was attempted. Dimensions only: no address, phone, name or id.
  const trackReport = (outcome: 'sent' | 'duplicate_warned' | 'failed', errorCode = 'none') => {
    track('address_mismatch_reported', { outcome, error_code: errorCode, carrier: carrierToken });
  };

  // No confirmation step: the CTA's click is the send. Both outcomes that leave the tracking in the
  // sheet are remembered for the session, so the button does not come back.
  const submit = () => {
    dispatch({ type: 'SUBMIT' });
    mutation.mutate(report, {
      onSuccess: (outcome) => {
        if (outcome.kind === 'duplicate') {
          check.markReported();
          dispatch({ type: 'DUPLICATE' });
          trackReport('duplicate_warned');
          return;
        }
        check.markReported();
        dispatch({ type: 'SUCCESS', row: outcome.row });
        trackReport('sent');
      },
      onError: (err) => {
        const code = err instanceof ApiError ? err.code : null;
        dispatch({ type: 'ERROR', code });
        trackReport('failed', code ?? 'unknown');
      },
    });
  };

  return (
    <AddressMismatchAlert
      report={report}
      state={state}
      checking={check.checking}
      alreadyReported={check.alreadyReported}
      {...(reportAddressMismatch ? { onReport: submit } : {})}
      onFailureReported={() => {
        dispatch({ type: 'REPORTED' });
      }}
    />
  );
}
